import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { CheckCircle, ChevronLeft, Quiz } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import color from "../../../components/utils/Colors";
import { QnsSortBy, QnsTypeSortBy } from "../../../components/utils/Data";
import {
  createAnswer,
  getCourseQuestions,
  getUserAnswers,
  QuestionRecord,
} from "../../../services/services";
import { useSnackbar } from "../../../components/utils/SnackBar";

interface CourseContent {
  id: number | string;
  lectureName?: string;
  lectureOrder?: number;
}

interface LecturePaperGroup {
  contentId: number | string;
  lectureName: string;
  questions: QuestionRecord[];
}

interface QnsPaperTabProps {
  courseId: string;
  contents: CourseContent[];
}

const getUserIdFromToken = (): number | null => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    const decoded: any = jwtDecode(token);
    const id = decoded.id || decoded.userId || decoded.sub;
    return id ? Number(id) : null;
  } catch {
    return null;
  }
};

const mapApiTypeToLabel = (type: string): string => {
  const map: Record<string, string> = {
    mcq: "MCQ",
    "yes/no": "Yes/No",
    short_answer: "Short_Answer",
    fill_in_the_blank: "Fill_in_the_blank",
  };
  return map[type] || type;
};

const mapLabelToApiType = (label: string): string => {
  const map: Record<string, string> = {
    MCQ: "mcq",
    "Yes/No": "yes/no",
    Short_Answer: "short_answer",
    Fill_in_the_blank: "fill_in_the_blank",
    "Short Questions": "short_answer",
    "Fill in the blanks": "fill_in_the_blank",
  };
  return map[label] || label.toLowerCase();
};

const QnsPaperTab = ({ courseId, contents }: QnsPaperTabProps) => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const userId = getUserIdFromToken();

  const [loading, setLoading] = useState(true);
  const [allQuestions, setAllQuestions] = useState<QuestionRecord[]>([]);
  const [selectedLecture, setSelectedLecture] =
    useState<LecturePaperGroup | null>(null);
  const [sortBy, setSortBy] = useState(QnsSortBy[0]);
  const [questionTypeFilter, setQuestionTypeFilter] = useState("All");
  const [draftAnswers, setDraftAnswers] = useState<Record<number, string>>({});
  const [submittedAnswers, setSubmittedAnswers] = useState<
    Record<number, string>
  >({});
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const showSnackbar = useSnackbar();

  const fetchQuestions = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await getCourseQuestions({ courseId, status: "assigned" });
      const items: QuestionRecord[] = res?.data?.data || [];
      setAllQuestions(items);
    } catch (err) {
      console.error("Failed to fetch questions:", err);
      setAllQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const lectureGroups = useMemo((): LecturePaperGroup[] => {
    const byContent = new Map<number | string, QuestionRecord[]>();
    allQuestions.forEach((q) => {
      const key = q.contentId;
      if (!byContent.has(key)) byContent.set(key, []);
      byContent.get(key)!.push(q);
    });

    const contentMap = new Map(
      contents.map((c) => [String(c.id), c.lectureName || `Lecture ${c.id}`]),
    );

    const groups: LecturePaperGroup[] = [];
    byContent.forEach((questions, contentId) => {
      groups.push({
        contentId,
        lectureName:
          contentMap.get(String(contentId)) || `Lecture ${contentId}`,
        questions,
      });
    });

    return groups.sort((a, b) => {
      const orderA =
        contents.find((c) => String(c.id) === String(a.contentId))
          ?.lectureOrder ?? 0;
      const orderB =
        contents.find((c) => String(c.id) === String(b.contentId))
          ?.lectureOrder ?? 0;
      return orderA - orderB;
    });
  }, [allQuestions, contents]);

  const loadExistingAnswers = useCallback(
    async (questionIds: number[]) => {
      if (!userId || questionIds.length === 0) return;
      try {
        const res = await getUserAnswers({ userId });
        const answers: { questionId: number; userOutcome: string }[] =
          res?.data?.data || [];
        const relevant = answers.filter((a) =>
          questionIds.includes(Number(a.questionId)),
        );
        const map: Record<number, string> = {};
        relevant.forEach((a) => {
          map[Number(a.questionId)] = a.userOutcome;
        });
        setSubmittedAnswers(map);
        setDraftAnswers(map);
      } catch (err) {
        console.error("Failed to fetch existing answers:", err);
      }
    },
    [userId],
  );

  const openLecturePaper = (group: LecturePaperGroup) => {
    setSelectedLecture(group);
    setQuestionTypeFilter("All");
    loadExistingAnswers(group.questions.map((q) => q.id));
  };

  const closeLecturePaper = () => {
    setSelectedLecture(null);
  };

  const filteredQuestions = useMemo(() => {
    if (!selectedLecture) return [];
    let list = [...selectedLecture.questions];
    if (questionTypeFilter !== "All") {
      const apiType = mapLabelToApiType(questionTypeFilter);
      list = list.filter((q) => q.questionType === apiType);
    }
    if (sortBy === "Sort by most recent") {
      list.sort((a, b) => b.id - a.id);
    }
    return list;
  }, [selectedLecture, questionTypeFilter, sortBy]);

  const handleSubmitAnswer = async (question: QuestionRecord) => {
    const answer = draftAnswers[question.id]?.trim();
    if (!answer) {
      showSnackbar("Please provide an answer before submitting.");
      return;
    }
    if (!userId) {
      showSnackbar("Please log in to submit your answer.");
      return;
    }

    setSubmittingId(question.id);

    try {
      await createAnswer({
        userOutcome: answer,
        questionId: question.id,
        courseId: parseInt(courseId) || courseId,
        courseContentId: question.contentId,
        userId,
        status: "submitted",
      });
      setSubmittedAnswers((prev) => ({ ...prev, [question.id]: answer }));
      showSnackbar(`Answer submitted for question ${question.id}.`);
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          "Failed to submit answer. Please try again.",
      );
    } finally {
      setSubmittingId(null);
    }
  };

  const renderMcqOptions = (question: QuestionRecord, index: number) => {
    const options = question.options || [];
    const selected = draftAnswers[question.id];

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "16px",
          mt: 1,
        }}
      >
        {options.map((opt, optionIndex) => {
          const isSelected = selected === opt;
          return (
            <Box
              key={optionIndex}
              onClick={() =>
                setDraftAnswers((prev) => ({ ...prev, [question.id]: opt }))
              }
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                width: isSmallScreen ? "100%" : "40%",
                background: isSelected
                  ? "rgba(0, 128, 0, 0.183)"
                  : "transparent",
                cursor: "pointer",
                borderRadius: "5px",
                padding: "5px",
              }}
            >
              <span
                style={{
                  marginRight: 8,
                  background: isSelected ? "darkgreen" : color.firstColor,
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  textAlign: "center",
                  paddingTop: "3.5px",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                {String.fromCharCode(65 + optionIndex)}
              </span>
              <Typography sx={{ flex: 1 }}>{opt}</Typography>
              {isSelected && <CheckCircle style={{ color: "green" }} />}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderYesNoOptions = (question: QuestionRecord) => {
    const selected = draftAnswers[question.id]?.toLowerCase();

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "16px",
          mt: 1,
        }}
      >
        {["Yes", "No"].map((opt, optionIndex) => {
          const isSelected = selected === opt.toLowerCase();
          return (
            <Box
              key={opt}
              onClick={() =>
                setDraftAnswers((prev) => ({
                  ...prev,
                  [question.id]: opt.toLowerCase(),
                }))
              }
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                width: isSmallScreen ? "100%" : "40%",
                background: isSelected
                  ? "rgba(0, 128, 0, 0.183)"
                  : "transparent",
                cursor: "pointer",
                borderRadius: "5px",
                padding: "5px",
              }}
            >
              <span
                style={{
                  marginRight: 8,
                  background: isSelected ? "darkgreen" : color.firstColor,
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  textAlign: "center",
                  paddingTop: "3.5px",
                  color: "white",
                }}
              >
                {String.fromCharCode(65 + optionIndex)}
              </span>
              <Typography>{opt}</Typography>
              {isSelected && <CheckCircle style={{ color: "green" }} />}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderTextAnswer = (question: QuestionRecord, multiline = false) => (
    <TextField
      className="input-root2"
      fullWidth
      variant="outlined"
      placeholder="Write your answer here"
      multiline={multiline}
      rows={multiline ? 2 : 1}
      value={draftAnswers[question.id] || ""}
      onChange={(e) =>
        setDraftAnswers((prev) => ({
          ...prev,
          [question.id]: e.target.value,
        }))
      }
      sx={{
        backgroundColor: "white",
        borderRadius: "14px",
        mt: 1,
      }}
    />
  );

  const renderQuestion = (question: QuestionRecord, index: number) => {
    const isSubmitted = Boolean(submittedAnswers[question.id]);
    const isSubmitting = submittingId === question.id;

    return (
      <Box
        key={question.id}
        sx={{
          border: "2px solid",
          borderRadius: "12px",
          p: 2,
          pb: 3,
          borderColor: color.borderColor,
          mb: 3,
        }}
      >
        <Grid container spacing={2} alignItems="center" sx={{ p: 1 }}>
          <Grid item xs={12} sx={{ display: "flex", color: color.firstColor }}>
            <Typography sx={{ fontWeight: "bold", mr: 0.5 }}>
              {index + 1}.
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>{question.name}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="caption"
              sx={{ color: "gray", fontWeight: 600 }}
            >
              Type: {mapApiTypeToLabel(question.questionType)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            {question.questionType === "mcq" &&
              renderMcqOptions(question, index)}
            {question.questionType === "yes/no" && renderYesNoOptions(question)}
            {question.questionType === "short_answer" &&
              renderTextAnswer(question, true)}
            {question.questionType === "fill_in_the_blank" &&
              renderTextAnswer(question)}
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              mt: { xs: 1, md: 1 },
            }}
          >
            {isSubmitted && (
              <Typography
                variant="caption"
                sx={{ color: "green", fontWeight: 600 }}
              >
                Submitted
              </Typography>
            )}
            <Button
              id="button"
              disabled={isSubmitting || isSubmitted}
              onClick={() => handleSubmitAnswer(question)}
              style={{
                backgroundColor: color.firstColor,
                color: "#fff",
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : isSubmitted ? (
                "Submitted"
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!selectedLecture) {
    return (
      <Box sx={{ p: 2, mt: 2 }}>
        <Typography
          sx={{
            color: color.textColor,
            fontWeight: "600",
            fontSize: { xs: "18px", sm: "20px", md: "24px" },
            mb: 3,
          }}
        >
          Lecture Question Papers
        </Typography>

        {lectureGroups.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              border: "2px dashed",
              borderColor: color.borderColor,
              borderRadius: "12px",
            }}
          >
            <Quiz sx={{ fontSize: 48, color: color.firstColor, mb: 1 }} />
            <Typography sx={{ color: "gray" }}>
              No question papers available for this course yet.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {lectureGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={String(group.contentId)}>
                <Box
                  onClick={() => openLecturePaper(group)}
                  sx={{
                    border: "2px solid",
                    borderColor: color.borderColor,
                    borderRadius: "12px",
                    p: 3,
                    cursor: "pointer",
                    background: color.secondColor,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: color.firstColor,
                      boxShadow: "0 4px 12px rgba(46, 10, 87, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Quiz sx={{ color: color.firstColor }} />
                    <Typography
                      sx={{
                        color: color.textColor,
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {group.lectureName}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: color.firstColor, fontWeight: 600 }}>
                    {group.questions.length} question
                    {group.questions.length !== 1 ? "s" : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
                    Click to open and answer
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mt: 2 }}>
      <Typography
        onClick={closeLecturePaper}
        sx={{
          color: color.firstColor,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          mb: 2,
          width: "fit-content",
        }}
      >
        <ChevronLeft /> Back to question papers
      </Typography>

      <Typography
        sx={{
          color: color.textColor,
          fontWeight: "600",
          fontSize: { xs: "18px", sm: "20px", md: "24px" },
          mb: 2,
        }}
      >
        {selectedLecture.lectureName}
      </Typography>

      <Typography
        sx={{
          color: color.textColor,
          fontWeight: "600",
          fontSize: { xs: "18px", sm: "20px", md: "24px" },
          textDecoration: "underline",
          mb: 2,
        }}
      >
        Questions
      </Typography>

      {filteredQuestions.length === 0 ? (
        <Typography sx={{ color: "gray", py: 4, textAlign: "center" }}>
          No questions match the selected filter.
        </Typography>
      ) : (
        filteredQuestions.map((q, idx) => renderQuestion(q, idx))
      )}
    </Box>
  );
};

export default QnsPaperTab;
